/* global React, Icon, t, PlummeI18n, HelpArticleBlocks */
// Rich help articles opened through window.openHelpArticle(id).
const { useState: haUseState, useEffect: haUseEffect } = React;

// Build on demand so article translations follow the active locale.
// Domain packs register builders in window.PlummeHelpArticlePacks.
const bundledHelpArticles = () => Object.assign(baseHelpArticles(),
  ...(window.PlummeHelpArticlePacks || []).map((pack) => pack()));

// Synchronous registry {id: article}. Published Firestore articles (helpArticleStore.js)
// win when the store holds a valid list for the active locale; otherwise the bundled
// articles are used. The two sources are never mixed.
const helpArticles = () => {
  const store = window.PlummeHelpStore;
  if (!store) return bundledHelpArticles();
  store.ensure();
  return store.getRegistry() || bundledHelpArticles();
};

const baseHelpArticles = () => ({
  h6: {
    id: 'h6', title: t('ha.h6.title'), category: t('ha.cat.vat'), read: t('ha.read3'),
    route: 'innsendinger',
    lead: t('ha.h6.lead'),
    blocks: [
    { t: 'heading', text: t('ha.h6.before') },
    { t: 'para', text: t('ha.h6.beforeText') },
    { t: 'tip', kind: 'info', title: t('ha.h6.reminded'), text: t('ha.h6.remindedText') },

    { t: 'step', n: 1, title: t('ha.h6.openPeriod'), text: t('ha.h6.openPeriodText') },
    { t: 'figure', src: 'src/img/help/mva-terminer.png', caption: t('ha.h6.fig1') },

    { t: 'step', n: 2, title: t('ha.h6.check'), text: t('ha.h6.checkText') },
    { t: 'figure', src: 'src/img/help/mva-beregning.png', caption: t('ha.h6.fig2') },

    { t: 'step', n: 3, title: t('ha.h6.codes'), text: t('ha.h6.codesText') },
    { t: 'figure', src: 'src/img/help/mva-koder.png', caption: t('ha.h6.fig3') },
    { t: 'tip', kind: 'warn', title: t('ha.h6.deviation'), text: t('ha.h6.deviationText') },

    { t: 'step', n: 4, title: t('ha.h6.send'), text: t('ha.h6.sendText') },

    { t: 'heading', text: t('ha.h6.after') },
    { t: 'para', text: t('ha.h6.afterText') }]

  },
  h12: { id: 'h12', title: t('ha.h12.title'), category: t('ha.cat.sales'), read: t('ha.read3'), route: 'faktura',
    lead: t('ha.h12.lead'),
    blocks: [
    { t: 'heading', text: t('ha.h6.before') },
    { t: 'para', text: t('ha.h12.beforeText') },
    { t: 'step', n: 1, title: t('ha.h12.open'), text: t('ha.h12.openText') },
    { t: 'tip', kind: 'info', title: t('ha.h12.quoteOrder'), text: t('ha.h12.quoteOrderText') },
    { t: 'step', n: 2, title: t('ha.h12.customer'), text: t('ha.h12.customerText') },
    { t: 'figure', src: 'src/img/help/faktura-kunde.png', caption: t('ha.h12.fig1') },
    { t: 'step', n: 3, title: t('ha.h12.details'), text: t('ha.h12.detailsText') },
    { t: 'figure', src: 'src/img/help/faktura-detaljer.png', caption: t('ha.h12.fig2') },
    { t: 'step', n: 4, title: t('ha.h12.lines'), text: t('ha.h12.linesText') },
    { t: 'figure', src: 'src/img/help/faktura-linjer.png', caption: t('ha.h12.fig3') },
    { t: 'tip', kind: 'info', title: t('ha.h12.livePreview'), text: t('ha.h12.livePreviewText') },
    { t: 'step', n: 5, title: t('ha.h12.preview'), text: t('ha.h12.previewText') },
    { t: 'figure', src: 'src/img/help/faktura-forhandsvisning.png', caption: t('ha.h12.fig4') },
    { t: 'para', text: t('ha.h12.afterText') }]
  },
  h13: { id: 'h13', title: t('ha.h13.title'), category: t('ha.cat.sales'), read: t('ha.read2'), route: 'faktura',
    lead: t('ha.h13.lead'),
    blocks: [
    { t: 'heading', text: t('ha.h13.when') },
    { t: 'para', text: t('ha.h13.whenText') },
    { t: 'step', n: 1, title: t('ha.h13.choose'), text: t('ha.h13.chooseText') },
    { t: 'step', n: 2, title: t('ha.h13.receipt'), text: t('ha.h13.receiptText') },
    { t: 'step', n: 3, title: t('ha.h13.details'), text: t('ha.h13.detailsText') },
    { t: 'figure', src: 'src/img/help/annetsalg-detaljer.png', caption: t('ha.h13.fig1') },
    { t: 'step', n: 4, title: t('ha.h12.lines'), text: t('ha.h13.linesText') },
    { t: 'figure', src: 'src/img/help/annetsalg-linjer.png', caption: t('ha.h13.fig2') },
    { t: 'step', n: 5, title: t('ha.h13.register'), text: t('ha.h13.registerText') },
    { t: 'tip', kind: 'info', title: t('ha.h13.invoiceOrOther'), text: t('ha.h13.invoiceOrOtherText') }]
  },
  h1: { id: 'h1', title: t('ha.h1.title'), category: t('ha.cat.sales'), read: t('ha.read2'), route: 'faktura',
    lead: t('ha.h1.lead'),
    blocks: [
    { t: 'step', n: 1, title: t('ha.h1.start'), text: t('ha.h1.startText') },
    { t: 'step', n: 2, title: t('ha.h1.turnOn'), text: t('ha.h1.turnOnText') },
    { t: 'figure', src: 'src/img/help/faktura-repeterende.png', caption: t('ha.h1.fig1') },
    { t: 'step', n: 3, title: t('ha.h1.save'), text: t('ha.h1.autoText') },
    { t: 'tip', kind: 'info', title: t('ha.h1.control'), text: t('ha.h1.controlText') }]
  },
  h2: { id: 'h2', title: t('ha.h2.title'), category: t('ha.cat.sales'), read: t('ha.read2'), route: 'fakturaliste',
    lead: t('ha.h2.lead'),
    blocks: [
    { t: 'step', n: 1, title: t('ha.h2.open'), text: t('ha.h2.openText') },
    { t: 'figure', src: 'src/img/help/faktura-betalingsstatus.png', caption: t('ha.h2.fig1') },
    { t: 'step', n: 2, title: t('ha.h2.register'), text: t('ha.h2.registerText') },
    { t: 'figure', src: 'src/img/help/faktura-registrer-betaling.png', caption: t('ha.h2.fig2') },
    { t: 'step', n: 3, title: t('ha.h2.handle'), text: t('ha.h2.handleText') },
    { t: 'tip', kind: 'info', title: t('ha.h2.autoStatus'), text: t('ha.h2.autoStatusText') }]
  },
  h9: { id: 'h9', title: t('ha.h9.title'), category: t('ha.cat.assets'), read: t('ha.read3'), route: 'eiendeler',
    lead: t('ha.h9.lead'),
    blocks: [
    { t: 'heading', text: t('ha.h9.whenAsset') },
    { t: 'para', text: t('ha.h9.whenAssetText') },
    { t: 'tip', kind: 'info', title: t('ha.h9.limit'), text: t('ha.h9.limitText') },
    { t: 'step', n: 1, title: t('ha.h9.register'), text: t('ha.h9.registerText') },
    { t: 'figure', src: 'src/img/help/h9-new.png', caption: t('ha.h9.register') },
    { t: 'step', n: 2, title: t('ha.h9.group'), text: t('ha.h9.groupText') },
    { t: 'step', n: 3, title: t('ha.h9.depreciate'), text: t('ha.h9.depreciateText') },
    { t: 'heading', text: t('ha.h9.sell') },
    { t: 'para', text: t('ha.h9.sellText') }]
  },
  h10: { id: 'h10', title: t('ha.h10.title'), category: t('ha.cat.budget'), read: t('ha.read2'), route: 'budsjett',
    lead: t('ha.h10.lead'),
    blocks: [
    { t: 'step', n: 1, title: t('ha.h10.method'), text: t('ha.h10.methodText') },
    { t: 'step', n: 2, title: t('ha.h10.amounts'), text: t('ha.h10.amountsText') },
    { t: 'step', n: 3, title: t('ha.h10.variance'), text: t('ha.h10.varianceText') },
    { t: 'tip', kind: 'tip', title: t('ha.h10.startSimple'), text: t('ha.h10.startSimpleText') }]
  },
  h11: { id: 'h11', title: t('ha.h11.title'), category: t('ha.cat.closing'), read: t('ha.read3'), route: 'arsavslutning',
    lead: t('ha.h11.lead'),
    blocks: [
    { t: 'heading', text: t('ha.h11.before') },
    { t: 'para', text: t('ha.h11.beforeText') },
    { t: 'step', n: 1, title: t('ha.h11.checklist'), text: t('ha.h11.checklistText') },
    { t: 'figure', src: 'src/img/help/h11-checklist.png', caption: t('ha.h11.checklist') },
    { t: 'step', n: 2, title: t('ha.h11.closeYear'), text: t('ha.h11.closeYearText') },
    { t: 'step', n: 3, title: t('ha.h11.taxReturn'), text: t('ha.h11.taxReturnText') },
    { t: 'step', n: 4, title: t('ha.h11.annualAccounts'), text: t('ha.h11.annualAccountsText') },
    { t: 'tip', kind: 'warn', title: t('ha.h11.reconciled'), text: t('ha.h11.reconciledText') }]
  }
});

function HelpArticle({ article, onClose, min, setMin }) {
  const [width, setWidth] = haUseState(() => {
    const v = parseInt(localStorage.getItem('plumme-ha-width'), 10);
    return v && v >= 420 ? v : 640;
  });
  const [expandedFigure, setExpandedFigure] = haUseState(null);
  const dragRef = React.useRef(null);
  const startResize = (e) => {
    e.preventDefault();
    const move = (ev) => {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const w = Math.max(420, Math.min(window.innerWidth * 0.96, window.innerWidth - x));
      setWidth(w);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = '';
      document.documentElement.classList.remove('ha-resizing');
      try { localStorage.setItem('plumme-ha-width', String(Math.round(dragRef.current || width))); } catch (err) {}
    };
    document.body.style.userSelect = 'none';
    document.documentElement.classList.add('ha-resizing');
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  React.useEffect(() => { dragRef.current = width; }, [width]);
  haUseEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (expandedFigure) setExpandedFigure(null);
      else if (!min) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedFigure, min]);
  // Push the app content aside (instead of covering it) while the article is open, so the
  // reader can follow the steps next to the page. Only when the content area keeps a usable
  // width; otherwise the panel overlays as before. Mobile (<=640px) is full-screen overlay.
  haUseEffect(() => {
    const root = document.documentElement;
    const clear = () => {root.classList.remove('ha-push');root.style.removeProperty('--ha-push');};
    const apply = () => {
      const main = document.querySelector('.main');
      const left = main ? main.getBoundingClientRect().left : 0;
      const fits = !min && window.innerWidth > 640 && window.innerWidth - left - width >= 480;
      if (!fits) {clear();return;}
      root.style.setProperty('--ha-push', width + 'px');
      root.classList.add('ha-push');
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {window.removeEventListener('resize', apply);clear();};
  }, [min, width]);
  // Rutenavnet er alltid det samme som i menyen — slaa det opp der framfor aa
  // duplisere det per artikkel.
  const routeLabel = article.route ? t(PlummeI18n.keyFor('nav', article.route, 'route')) : '';
  const goTo = () => {if (article.route && window.plummeNav) {window.plummeNav(article.route);if (window.toast) window.toast(t('ha.openedInBackground', { page: routeLabel }), 'info');}};
  const backToSearch = () => {setMin(true);if (window.openSearch) window.openSearch(window.__lastSearchQuery || '');};
  const firstStep = article.blocks.findIndex((b) => b.t === 'step');
  // Bundled fallback only: the overview image after the first step is injected here. In
  // DB articles it is a real figure block (seeded), so Admin can edit or remove it.
  const alias = { holidayPay: 'payrollRun', employeePortal: 'payrollRun', personalFinance: 'myPage' }[article.id] || article.id;
  const hasBuiltInOverview = article.source === 'db' || ['h1', 'h2', 'h6', 'h12', 'h13'].includes(article.id);
  const blocks = hasBuiltInOverview ? article.blocks : article.blocks.flatMap((b, i) => i === firstStep ? [b, { t: 'figure', src: `src/img/help/${alias}.png`, caption: b.title }] : [b]);

  return <React.Fragment>
    <div className={'ha-dialog' + (min ? ' ha-min' : '')} role="dialog" aria-label={article.title} style={min ? undefined : { width: width + 'px' }}>
      {!min && <div className="ha-resize" onPointerDown={startResize} title={t('ha.dragWidth')} aria-label={t('ha.changeWidth')}><span className="ha-resize-grip" /></div>}
      {min ?
      <div className="ha-minbar" role="button" tabIndex={0} title={t('ha.reopen')} onClick={() => setMin(false)} onKeyDown={(e) => {if (e.key === 'Enter' || e.key === ' ') setMin(false);}}>
            <span className="ha-minbar-ico"><Icon name="help" size={16} /></span>
            <span className="ha-minbar-text"><span className="ha-minbar-kicker">{t('ha.readOn')}</span><span className="ha-minbar-title">{article.title}</span></span>
            <span className="ha-minbar-actions">
              <span className="ha-minbar-exp"><Icon name="chevUp" size={16} /></span>
              <button className="ha-minbar-close" onClick={(e) => {e.stopPropagation();onClose();}} aria-label={t('ha.closeArticle')}><Icon name="x" size={15} /></button>
            </span>
          </div> :
      <div className="ha-toolbar">
            <button className="ha-tool" onClick={() => setMin(true)} aria-label={t('ha.minimize')} title={t('ha.minimizeHelp')}><Icon name="chevDown" size={20} /></button>
            <button className="ha-tool" onClick={onClose} aria-label={t('common.close')} title={t('common.close')}><Icon name="x" size={20} /></button>
          </div>}
      <div className="ha-scroll" style={min ? { display: 'none' } : undefined}>
        <div className="ha-head">
          <button className="ha-back" onClick={backToSearch}><Icon name="arrowLeft" size={16} />{t('ha.backToSearch')}</button>
          <div className="ha-eyebrow"><Icon name="help" size={14} />{t('ha.eyebrow', { category: article.category })}</div>
          <h1 className="ha-title">{article.title}</h1>
          <div className="ha-meta"><Icon name="clock" size={14} />{article.read}</div>
          <p className="ha-lead">{article.lead}</p>
        </div>

        <div className="ha-body">
          <HelpArticleBlocks blocks={blocks} variant="drawer" onExpandFigure={setExpandedFigure} />
        </div>

        {article.route && <div className="ha-foot">
          <button className="ha-go" onClick={goTo}>{t('ha.goTo', { page: routeLabel })}<Icon name="arrowRight" size={17} /></button>
        </div>}
      </div>
    </div>
    {expandedFigure && <div className="ha-lightbox" role="dialog" aria-modal="true" aria-label={expandedFigure.caption} onClick={() => setExpandedFigure(null)}>
      <button type="button" className="ha-lightbox-close" onClick={() => setExpandedFigure(null)} aria-label={t('ha.collapseImage')} title={t('ha.collapseImage')}><Icon name="x" size={22} /></button>
      <button type="button" className="ha-lightbox-image" onClick={() => setExpandedFigure(null)} aria-label={t('ha.collapseImage')} title={t('ha.collapseImage')}>
        <img src={expandedFigure.src} alt={expandedFigure.caption} />
      </button>
      <div className="ha-lightbox-caption">{expandedFigure.caption}</div>
    </div>}
  </React.Fragment>;
}

function HelpArticleLayer() {
  window.useLocaleVersion?.(); // rebuild when the Firestore articles land
  const [id, setId] = haUseState(null);
  const [min, setMin] = haUseState(false);
  haUseEffect(() => {
    const h = (e) => {setId(e.detail);setMin(false);};
    window.addEventListener('plumme-help-article', h);
    return () => window.removeEventListener('plumme-help-article', h);
  }, []);
  if (!id) return null; // nothing is requested (or loaded) until an article is opened
  // An id missing from the active (DB) registry falls back to the bundled article, else nothing.
  const article = helpArticles()[id] || bundledHelpArticles()[id];
  if (!article) return null;
  return <HelpArticle key={id} article={article} min={min} setMin={setMin} onClose={() => {setId(null);setMin(false);}} />;
}

window.openHelpArticle = (id) => window.dispatchEvent(new CustomEvent('plumme-help-article', { detail: id }));
Object.assign(window, { helpArticles, HelpArticle, HelpArticleLayer });
